import type { User } from "firebase/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  Unsubscribe,
} from "firebase/firestore";
import { firestore } from "./firebaseClient";

const VES_PAYMENT_REQUESTS_COLLECTION = "vesPaymentRequests";

const getBackendBaseUrl = (): string => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (!raw) throw new Error("Missing REACT_APP_BACKEND_URL");
  return raw.replace(/\/+$/, "");
};

export interface VesPaymentRequestRecord {
  id: string;
  organizationId: string;
  organizationName?: string;
  plan: string;
  amountUSD: number;
  amountVES: number;
  exchangeRate: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureUrl: string;
  storagePath?: string;
  status: "pending" | "approved" | "rejected";
  product?: "base_plan" | "intelligence" | "intelligence_pack" | string;
  capacityId?: string;
  packId?: string;
  packName?: string;
  packCredits?: number;
  packQuantity?: number;
  createdBy?: string;
  reviewerId?: string;
  rejectionReason?: string;
  createdAt: Date | null;
  reviewedAt: Date | null;
}

const mapTimestamp = (value: unknown): Date | null => {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
};

const mapRecord = (id: string, data: Record<string, unknown>): VesPaymentRequestRecord => ({
  id,
  organizationId: typeof data.organizationId === "string" ? data.organizationId : "",
  organizationName: typeof data.organizationName === "string" ? data.organizationName : undefined,
  plan: typeof data.plan === "string" ? data.plan : "",
  amountUSD: typeof data.amountUSD === "number" ? data.amountUSD : 0,
  amountVES: typeof data.amountVES === "number" ? data.amountVES : 0,
  exchangeRate: typeof data.exchangeRate === "number" ? data.exchangeRate : 0,
  exchangeSourceId: typeof data.exchangeSourceId === "string" ? data.exchangeSourceId : undefined,
  exchangeSourceName: typeof data.exchangeSourceName === "string" ? data.exchangeSourceName : undefined,
  captureUrl: typeof data.captureUrl === "string" ? data.captureUrl : "",
  storagePath: typeof data.storagePath === "string" ? data.storagePath : undefined,
  status: (["pending", "approved", "rejected"].includes(data.status as string) ? data.status : "pending") as VesPaymentRequestRecord["status"],
  product:
    typeof data.product === "string" && data.product.trim()
      ? data.product.trim()
      : "base_plan",
  capacityId: typeof data.capacityId === "string" ? data.capacityId : undefined,
  packId: typeof data.packId === "string" ? data.packId : undefined,
  packName: typeof data.packName === "string" ? data.packName : undefined,
  packCredits: typeof data.packCredits === "number" ? data.packCredits : undefined,
  packQuantity: typeof data.packQuantity === "number" ? data.packQuantity : undefined,
  createdBy: typeof data.createdBy === "string" ? data.createdBy : undefined,
  reviewerId: typeof data.reviewerId === "string" ? data.reviewerId : undefined,
  rejectionReason: typeof data.rejectionReason === "string" ? data.rejectionReason : undefined,
  createdAt: mapTimestamp(data.createdAt),
  reviewedAt: mapTimestamp(data.reviewedAt),
});

/**
 * Subscribe to pending VES payment requests.
 */
export const subscribeToPendingVesPayments = (
  onRecords: (records: VesPaymentRequestRecord[]) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  return subscribeToVesPayments(onRecords, onError, "pending");
};

/**
 * Subscribe to VES payment requests (optionally filtered by status).
 */
export const subscribeToVesPayments = (
  onRecords: (records: VesPaymentRequestRecord[]) => void,
  onError: (error: Error) => void,
  statusFilter?: VesPaymentRequestRecord["status"]
): Unsubscribe => {
  const ref = collection(firestore, VES_PAYMENT_REQUESTS_COLLECTION);
  const q = statusFilter
    ? query(ref, where("status", "==", statusFilter))
    : ref;

  return onSnapshot(
    q,
    (snapshot) => {
      const records = snapshot.docs
        .map((d) => mapRecord(d.id, d.data() as Record<string, unknown>))
        .sort((a, b) => {
          const ta = a.createdAt?.getTime() ?? 0;
          const tb = b.createdAt?.getTime() ?? 0;
          return tb - ta;
        });
      onRecords(records);
    },
    (err) => onError(err)
  );
};

/**
 * Approve a VES payment request via backend.
 * Backend assigns plan limits from database; keeps current limits if org has Custom plan.
 */
export const approveVesPayment = async (
  requestId: string,
  organizationId: string,
  user: User,
  storagePath?: string
): Promise<void> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/payments/ves-approve`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      requestId,
      organizationId,
      storagePath: storagePath || undefined,
    }),
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Error ${response.status}`;
    throw new Error(detail);
  }
};

/**
 * Reject a VES payment request via backend.
 * Backend sets request to rejected and org billing to ves_rejected (blocks user).
 */
export const rejectVesPayment = async (
  requestId: string,
  organizationId: string,
  user: User,
  rejectionReason?: string
): Promise<void> => {
  const token = await user.getIdToken();
  const baseUrl = getBackendBaseUrl();
  const response = await fetch(`${baseUrl}/api/v1/payments/ves-reject`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      requestId,
      organizationId,
      rejectionReason: rejectionReason || undefined,
    }),
  });
  const data = (await response.json().catch(() => null)) as unknown;
  if (!response.ok) {
    const detail =
      data && typeof data === "object" && "detail" in data && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : `Error ${response.status}`;
    throw new Error(detail);
  }
};

